import { useState, useRef, useEffect, useCallback } from 'react';
import type { KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowUp } from 'lucide-react';
import CONFIG from '../../config';
import { cn } from '@/lib/utils';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled: boolean;
  onInputHeightChange?: (isMultiRow: boolean) => void;
  value?: string;
  onInputChange?: (value: string) => void;
}

export const ChatInput = ({
  onSend,
  disabled,
  onInputHeightChange,
  value: externalValue,
  onInputChange,
}: ChatInputProps) => {
  const [internalInput, setInternalInput] = useState('');
  const input = externalValue !== undefined ? externalValue : internalInput;
  const setInput = onInputChange || setInternalInput;
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Character limit calculations
  const maxLength = CONFIG.MAX_MESSAGE_LENGTH;
  const charCount = input.length;
  const isOverLimit = charCount > maxLength;
  const isNearLimit = charCount >= maxLength * 0.8;
  const showCounter = charCount >= maxLength * 0.7;

  // Auto-resize textarea
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Calculate new height (min 44px, max 200px)
    const newHeight = Math.min(Math.max(textarea.scrollHeight, 44), 200);
    textarea.style.height = `${newHeight}px`;

    // Notify parent if input is multiline
    const isMultiLine = newHeight > 52;
    onInputHeightChange?.(isMultiLine);
  }, [onInputHeightChange]);

  // Adjust height when input changes
  useEffect(() => {
    adjustHeight();
  }, [input, adjustHeight]);

  const handleSend = () => {
    const text = input.trim();
    if (text && !disabled && !isOverLimit) {
      onSend(text);
      setInput('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.blur();
      }
      onInputHeightChange?.(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  return (
    <footer className="w-full px-4 pb-4" data-testid="chat-input">
      <div
        className={cn(
          'relative flex items-end gap-2 w-full rounded-lg border bg-muted p-2 transition-all duration-200',
          isFocused
            ? 'border-ring ring-2 ring-ring/20'
            : 'border-border hover:border-border/80',
          disabled && 'opacity-60 cursor-not-allowed',
        )}
      >
        {/* Textarea input */}
        <textarea
          ref={textareaRef}
          value={input}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          placeholder="Ask about sales trends..."
          rows={1}
          className={cn(
            'flex-1 resize-none bg-transparent px-2 py-2 text-base leading-6',
            'placeholder:text-muted-foreground/70',
            'focus:outline-none focus:ring-0',
            'disabled:cursor-not-allowed',
            'min-h-[44px] max-h-[200px]',
          )}
          style={{ height: 'auto' }}
          aria-label="Message input"
          aria-describedby={showCounter ? 'chat-char-count' : undefined}
        />

        {/* Right side: counter and send button */}
        <div className="flex items-end gap-2 pb-1">
          {/* Character counter */}
          <div
            id="chat-char-count"
            className={cn(
              'text-xs tabular-nums transition-opacity duration-200 pb-2',
              showCounter ? 'opacity-100' : 'opacity-0',
              isOverLimit
                ? 'text-destructive font-medium'
                : isNearLimit
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-muted-foreground',
            )}
            aria-live="polite"
            aria-atomic="true"
          >
            {showCounter && (
              <span>
                {charCount.toLocaleString()}/{maxLength.toLocaleString()}
              </span>
            )}
          </div>

          {/* Send button */}
          <Button
            onClick={handleSend}
            disabled={disabled || !input.trim() || isOverLimit}
            size="icon"
            className="shrink-0 rounded-md h-9 w-9"
            aria-label="Send message"
            title={isOverLimit ? 'Message too long' : 'Send message'}
          >
            <ArrowUp className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </footer>
  );
};
